import { Stack } from '@mui/joy';
import { Check, Copy, Eye } from '@phosphor-icons/react';
import { ICellRendererParams } from 'ag-grid-community';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { useCopyToClipboard } from 'core/hooks';
import { useGetSubmitOrderLogEntry } from 'dashboard/hooks';
import { ISubmitOrderLogEntry } from 'dashboard/types';
import { Color, Direction, Variant } from 'theme/enums';

import { PayloadPanel } from '../PayloadPanel';
import { payloadToText } from '../utils';

export const PayloadCell = ({ data }: ICellRendererParams<ISubmitOrderLogEntry>) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const { t } = useTranslation();

  const { isCopied, copy } = useCopyToClipboard();

  const { getSubmitOrderLogEntry, isLoading } = useGetSubmitOrderLogEntry();

  if (!data) {
    return null;
  }

  const onCopy = async () => {
    if (isLoading) {
      return;
    }

    try {
      // The payload is fetched on demand: bodies run to tens/hundreds of KB per
      // entry, so the list endpoint deliberately doesn't carry them. The hook
      // rejects (with a notification) when no payload was captured.
      const entry = await getSubmitOrderLogEntry(data.id);

      await copy(payloadToText(entry.requestBody));
    } catch (error) {
      console.error('Failed to copy the request payload', error);
    }
  };

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', height: '100%' }}>
      <Button
        icon={<Eye />}
        variant={Variant.PLAIN}
        color={Color.NEUTRAL}
        onClick={() => setIsPanelOpen(true)}
        tooltipProps={{ title: t('tooltipViewPayload'), children: null }}
      />

      <Button
        icon={isCopied ? <Check /> : <Copy />}
        variant={Variant.PLAIN}
        color={isCopied ? Color.SUCCESS : Color.NEUTRAL}
        loading={isLoading}
        disabled={isLoading}
        onClick={onCopy}
        tooltipProps={{
          title: isCopied ? t('buttonLabelCopied') : t('tooltipCopyPayload'),
          children: null,
        }}
      />

      {/* Rendered by the cell (not the page) so the row it belongs to is implicit:
          the panel reads the entry it was opened from, no state threaded up. Mounted
          only while open — one drawer per rendered row would otherwise sit in the DOM. */}
      {isPanelOpen && <PayloadPanel entry={data} isOpen onClose={() => setIsPanelOpen(false)} />}
    </Stack>
  );
};
